import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Alert, CircularProgress,
  IconButton, Paper, Snackbar,
} from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { CustomButton, CustomInput } from '../../shared/components';
import CryptoJS from 'crypto-js';
import pidilite_logo from "../../Assets/Images/pidilite_logo.png";
import { getNodeServerUrl } from '../../utils/getNodeUrl';

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

function decryptParam(encryptedData, key) {
    try {
        const decoded = decodeURIComponent(encryptedData);
        let bytes = CryptoJS.AES.decrypt(decoded, key);
        let decrypted = bytes.toString(CryptoJS.enc.Utf8);
        return decrypted;
    } catch (error) {
        try {
            let bytes = CryptoJS.AES.decrypt(encryptedData, key);
            let decrypted = bytes.toString(CryptoJS.enc.Utf8);
            return decrypted;
        } catch (retryError) {
            return '';
        }
    }
}

const FeedbackForm = () => {
    const query = useQuery();
    const navigate = useNavigate();
    const secretKey = 'ThisIsASecretKey';

    const [meetingId, setMeetingId] = useState('');
    const [dealerCode, setDealerCode] = useState('');
    const [participantName, setParticipantName] = useState('');
    const [experienceRating, setExperienceRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [likedAboutMeet, setLikedAboutMeet] = useState('');
    const [improvementFeedback, setImprovementFeedback] = useState('');

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [failedOpen, setFailedOpen] = useState(false);
    const [snackError, setSnackError] = useState("");
    const [errors, setErrors] = useState({});
    const [checkingStatus, setCheckingStatus] = useState(false);

    useEffect(() => {
        const encryptedId = query.get('id');
        const dealerCodeParam = query.get('dealer');

        if (encryptedId) {
            try {
                const decryptedId = decryptParam(encryptedId, secretKey);
                
                if (decryptedId && decryptedId.trim()) {
                    setMeetingId(decryptedId);
                } else {
                    setSnackError('Invalid QR code. Failed to decrypt meeting ID. Please scan again.');
                    setFailedOpen(true);
                }
            } catch (err) {
                setSnackError('Invalid QR code. Failed to decrypt meeting ID. Please scan again.');
                setFailedOpen(true);
            }
        } else {
            setSnackError('Invalid QR code. Missing meeting ID. Please scan again.');
            setFailedOpen(true);
        }

        if (dealerCodeParam) {
            setDealerCode(dealerCodeParam);
        }
    }, [query]);

    // Check if feedback already exists
    useEffect(() => {
        const checkFeedbackStatus = async () => {
            if (!meetingId || !dealerCode) return;

            setCheckingStatus(true);
            
            try {
                const response = await fetch(`${getNodeServerUrl()}/DRM_Feedback/checkStatus`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ 
                        meetingId, 
                        dealerCode: dealerCode.trim() 
                    })
                });

                const data = await response.json();

                if (response.ok && data.success && data.hasFeedback) {
                    // Feedback already submitted - redirect to Thank You page
                    navigate('/DRMThankYou');
                }
            } catch (error) {
                // Silently fail - let user try to submit, backend will handle duplicate
            } finally {
                setCheckingStatus(false);
            }
        };

        checkFeedbackStatus();
    }, [meetingId, dealerCode, navigate]);

    const handleOnClose = (reason) => {
        if (reason === "clickaway") {
            return;
        }
        setFailedOpen(false);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!meetingId) newErrors.meetingId = "Meeting ID is missing. Please scan the QR code again.";
        if (!dealerCode.trim()) newErrors.dealerCode = "Dealer Code is required";
        if (!participantName.trim()) newErrors.participantName = "Participant Name is required";
        if (experienceRating < 1 || experienceRating > 5) newErrors.experienceRating = "Please rate your experience (1-5 stars)";
        if (!likedAboutMeet.trim()) newErrors.likedAboutMeet = "Please share what you liked";
        if (!improvementFeedback.trim()) newErrors.improvementFeedback = "Please provide feedback for improvement";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${getNodeServerUrl()}/DRM_Feedback/createFeedback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    meetingId,
                    dealerCode: dealerCode.trim(),
                    participantName: participantName.trim(),
                    experienceRating,
                    likedAboutMeet: likedAboutMeet.trim(),
                    improvementFeedback: improvementFeedback.trim()
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccessMessage('Feedback submitted successfully! Redirecting...');
                
                // Auto-redirect to Thank You page after 2 seconds
                setTimeout(() => {
                    navigate('/DRMThankYou');
                }, 2000);
            } else {
                if (response.status === 409) {
                    setSnackError("Feedback already submitted for this dealer at this meeting.");
                } else {
                    setSnackError(data.message || "Failed to submit feedback. Please try again.");
                }
                setFailedOpen(true);
            }
        } catch (error) {
            console.error("Error during API call:", error);
            setSnackError("Network error. Please check your connection and try again.");
            setFailedOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "#f5f5f5",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                px: 2,
                py: 3,
            }}
        >
            <Snackbar
                open={failedOpen}
                autoHideDuration={7000}
                onClose={handleOnClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{ top: { xs: 16, sm: 24 }, maxWidth: { xs: "calc(100% - 32px)", sm: 480 } }}
            >
                <Alert
                    severity="error"
                    onClose={() => handleOnClose()}
                    variant="filled"
                    sx={{ width: "100%", borderRadius: 2, boxShadow: 3 }}
                >
                    {snackError}
                </Alert>
            </Snackbar>

            {/* Logo */}
            <Box sx={{ mb: 3 }}>
                <img src={pidilite_logo} alt="Pidilite" style={{ height: 40, objectFit: "contain" }} />
            </Box>

            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    maxWidth: 520,
                    borderRadius: 4,
                    p: { xs: 2.5, sm: 3.5 },
                    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                }}
            >
                {/* Header */}
                <Box sx={{ mb: 3, textAlign: "center" }}>
                    <Typography variant="h5" fontWeight={700} sx={{ color: "#1a1a1a" }}>
                        Meeting Feedback
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
                        We value your feedback! Please share your experience.
                    </Typography>
                </Box>

                {/* Checking status banner */}
                {checkingStatus && (
                    <Alert
                        icon={<CircularProgress size={16} />}
                        severity="info"
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        Checking feedback status…
                    </Alert>
                )}

                {/* Success banner */}
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                        {successMessage}
                    </Alert>
                )}

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                    {/* Dealer Code */}
                    <CustomInput
                        label="Dealer Code"
                        required
                        size="medium"
                        value={dealerCode}
                        onChange={(e) => setDealerCode(e.target.value)}
                        disabled={loading || checkingStatus || !!successMessage}
                        placeholder="Enter dealer code"
                        error={!!errors.dealerCode}
                        errorMessage={errors.dealerCode}
                    />

                    {/* Participant Name */}
                    <CustomInput
                        label="Participant Name"
                        required
                        size="medium"
                        value={participantName}
                        onChange={(e) => setParticipantName(e.target.value)}
                        disabled={loading || checkingStatus || !!successMessage}
                        placeholder="Enter your name"
                        error={!!errors.participantName}
                        errorMessage={errors.participantName}
                    />

                    {/* Star Rating */}
                    <Box sx={{ my: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: "#1a1a1a" }}>
                            Rate Your Experience{" "}
                            <Typography component="span" color="error">*</Typography>
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                            {[1, 2, 3, 4, 5].map((star) => {
                                const filled = star <= (hoveredRating || experienceRating);
                                const isDisabled = loading || checkingStatus || !!successMessage;
                                return (
                                    <IconButton
                                        key={star}
                                        size="small"
                                        disabled={isDisabled}
                                        onClick={() => setExperienceRating(star)}
                                        onMouseEnter={() => setHoveredRating(star)}
                                        onMouseLeave={() => setHoveredRating(0)}
                                        sx={{ p: 0.25, color: filled ? "#FFB400" : "#ccc", transition: "color 0.15s" }}
                                        disableRipple={false}
                                    >
                                        {filled
                                            ? <StarRoundedIcon sx={{ fontSize: 36 }} />
                                            : <StarBorderRoundedIcon sx={{ fontSize: 36 }} />
                                        }
                                    </IconButton>
                                );
                            })}
                        </Box>
                        {errors.experienceRating && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                                {errors.experienceRating}
                            </Typography>
                        )}
                    </Box>

                    {/* What did you like */}
                    <CustomInput
                        label="What did you like about the meet?"
                        required
                        size="medium"
                        multiline
                        rows={4}
                        value={likedAboutMeet}
                        onChange={(e) => setLikedAboutMeet(e.target.value)}
                        disabled={loading || checkingStatus || !!successMessage}
                        placeholder="Share what you enjoyed or found valuable..."
                        error={!!errors.likedAboutMeet}
                        errorMessage={errors.likedAboutMeet}
                    />

                    {/* How can we improve */}
                    <CustomInput
                        label="How can we improve?"
                        required
                        size="medium"
                        multiline
                        rows={4}
                        value={improvementFeedback}
                        onChange={(e) => setImprovementFeedback(e.target.value)}
                        disabled={loading || checkingStatus || !!successMessage}
                        placeholder="Your suggestions for improvement..."
                        error={!!errors.improvementFeedback}
                        errorMessage={errors.improvementFeedback}
                    />

                    <Box sx={{ mt: 1.5 }}>
                        <CustomButton
                            type="submit"
                            fullWidth
                            disabled={loading || checkingStatus || !!successMessage}
                            loading={loading}
                        >
                            {checkingStatus ? 'Checking…' : loading ? 'Submitting…' : 'Submit'}
                        </CustomButton>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default FeedbackForm;
